// ABOUTME: Filter form of the audit log page; the period is sent as whole local days in ISO timestamps (HCP-112400).
import {Button, ControlledDatepicker, ControlledInput, ControlledSelect, Grid} from '@hm/fundamental'
import type {ControlledDatepickerValue} from '@hm/fundamental'
import React from 'react'
import {Controller, useForm} from 'react-hook-form'

import {COUNTRY_CONFIGURATION_BUSINESS_LINES} from '../../CountryConfigurations/constants'
import {AUDIT_ACTIONS, AUDITED_ENTITY_TYPES} from '../auditLogTypes'
import type {AuditAction, AuditLogFilters as Filters} from '../auditLogTypes'

type Period = [Date | null, Date | null]

type SelectFieldName = 'countryId' | 'businessLine' | 'entityType' | 'action'

type FormValues = Record<SelectFieldName, string> & {
  performedBy: string
  period: Period
}

const EMPTY_VALUES: FormValues = {
  countryId: '',
  businessLine: '',
  entityType: '',
  action: '',
  performedBy: '',
  period: [null, null],
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const endOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)

const toPeriod = (value: ControlledDatepickerValue): Period =>
  Array.isArray(value) && value.length === 2 ? [value[0], value[1]] : [null, null]

const toFilters = (values: FormValues): Filters => {
  const [from, to] = values.period
  const filters: Filters = {
    countryId: values.countryId,
    businessLine: values.businessLine,
    entityType: values.entityType,
    action: (values.action || undefined) as AuditAction | undefined,
    performedBy: values.performedBy.trim(),
    from: from ? startOfDay(from).toISOString() : undefined,
    to: to ? endOfDay(to).toISOString() : undefined,
  }
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value)) as Filters
}

const withAllOption = (values: readonly string[]) => [
  {value: '', label: 'All'},
  ...values.map((value) => ({value, label: value})),
]

type Props = {
  countries: string[]
  onSearch: (filters: Filters) => void
}

export const AuditLogFilters: React.FC<Props> = ({countries, onSearch}) => {
  const {control, handleSubmit, reset} = useForm<FormValues>({defaultValues: EMPTY_VALUES})

  const selects: Array<{name: SelectFieldName; label: string; testId: string; values: readonly string[]}> = [
    {name: 'countryId', label: 'Country', testId: 'audit-filter-country', values: countries},
    {
      name: 'businessLine',
      label: 'Business line',
      testId: 'audit-filter-business-line',
      values: COUNTRY_CONFIGURATION_BUSINESS_LINES.map(({label}) => label),
    },
    {name: 'entityType', label: 'Entity type', testId: 'audit-filter-entity-type', values: AUDITED_ENTITY_TYPES},
    {name: 'action', label: 'Action', testId: 'audit-filter-action', values: AUDIT_ACTIONS},
  ]

  const handleReset = () => {
    reset(EMPTY_VALUES)
    onSearch({})
  }

  return (
    <form onSubmit={handleSubmit((values) => onSearch(toFilters(values)))} noValidate>
      <Grid size={1} mdSize={2} lgSize={4}>
        {selects.map(({name, label, testId, values}) => (
          <Controller
            key={name}
            name={name}
            control={control}
            // ControlledSelect needs its internal ref to accept a selection, so field.ref is not passed.
            render={({field}) => (
              <ControlledSelect
                name={field.name}
                label={label}
                value={field.value}
                onChange={(value) => field.onChange(String(value))}
                values={withAllOption(values)}
                className="w-full"
                data-test-id={testId}
              />
            )}
          />
        ))}
        <Controller
          name="performedBy"
          control={control}
          render={({field}) => (
            <ControlledInput {...field} label="Performed by (user id)" data-test-id="audit-filter-performed-by" />
          )}
        />
        <Controller
          name="period"
          control={control}
          render={({field}) => (
            <ControlledDatepicker
              name={field.name}
              label="Period"
              value={field.value}
              onChange={(value) => field.onChange(toPeriod(value))}
              data-cy="audit-filter-period"
            />
          )}
        />
        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" data-test-id="audit-filter-search">
            Search
          </Button>
          <Button type="button" variant="secondary" onClick={handleReset} data-test-id="audit-filter-reset">
            Reset
          </Button>
        </div>
      </Grid>
    </form>
  )
}
